import { ObservableStore } from '@codewithdan/observable-store';
import { filter } from 'rxjs/operators';
import { v4 } from 'uuid';
import templateRestriction from '../../data/templateRestrictions';
import templates from '../../data/templates';
import { Kite, PageKey, Store } from '../types';
import { defaultKiteData } from './_defaults';
import { Draft, Editor, Session } from './Store';

export class Kites extends ObservableStore<Store> {
    stateChanged = this.stateChanged.pipe(filter((state) => !!state));

    constructor() {
        super({
            // logStateChanges: true,
            // stateSliceSelector: state => state.kites,
        });
    }

    public init(kites: Store['kites']) {
        this.setState(
            {
                kites: {
                    ...kites,
                },
            },
            'init_kites'
        );
    }

    selectKite(id: Kite['id']) {
        Draft.init(this.getState(true).kites[id]);
        Editor.isSaved(true);
        const currentPage: PageKey = Session.get().page;
        Session.setPage(currentPage === 'parts' ? 'parts' : 'kite');
    }

    previousKite() {
        const {
            draft: { id },
            kites,
        } = this.getState();
        const kiteKeys = Object.keys(kites);
        const currentKiteIndex = kiteKeys.indexOf(id);
        this.selectKite(
            currentKiteIndex > 0
                ? kiteKeys[currentKiteIndex - 1]
                : kiteKeys[kiteKeys.length - 1]
        );
    }

    nextKite() {
        const {
            draft: { id },
            kites,
        } = this.getState();
        const kiteKeys = Object.keys(kites);
        const currentKiteIndex = kiteKeys.indexOf(id);
        this.selectKite(
            currentKiteIndex < kiteKeys.length - 1
                ? kiteKeys[currentKiteIndex + 1]
                : kiteKeys[0]
        );
    }

    addKite(template: keyof typeof templates, variation?: string) {
        const kiteIndices = Object.keys(this.getState().kites);
        const id = v4();

        const nextVariation = variation
            ? variation
            : templates[template].variations
            ? templates[template].variations[0]
            : undefined;

        const nextKite = {
            ...defaultKiteData,
            name: `new-${template}${nextVariation ? `-${nextVariation}` : ''}-${
                kiteIndices.length
            }`,
            svg: templates[template].svg,
            template,
            variation: nextVariation,
            id,
        };

        this.setState(
            {
                kites: {
                    ...this.getState(true).kites,
                    [id]: nextKite,
                },
            },
            'add_kite'
        );

        if (
            templateRestriction[nextKite.template] &&
            templateRestriction[nextKite.template].materials &&
            templateRestriction[nextKite.template].materials.length
        ) {
            Editor.selectMaterial(
                templateRestriction[nextKite.template].materials[0]
            );
        }

        Draft.init(nextKite);
        Session.setPage('kite');
    }

    removeKite(id: Kite['id']) {
        const kites = this.getState(true).kites;
        delete kites[id];
        this.setState({ kites });
        if (Object.keys(kites).length) this.selectKite(Object.keys(kites)[0]);
    }

    saveKite(id: Kite['id']) {
        const { draft, kites } = this.getState(true);
        this.setState(
            {
                kites: {
                    ...kites,
                    [id]: draft,
                },
            },
            'save_kite'
        );
        Editor.isSaved(true);
    }

    copyKite() {
        let nextKite = this.getState(true).draft;
        const id = v4();
        nextKite = {
            ...nextKite,
            id,
            name: nextKite.name + ' *',
        };
        this.setState(({ kites }) => ({
            kites: {
                ...kites,
                [id]: nextKite,
            },
        }));
        this.selectKite(id);
    }

    get() {
        return this.getState().kites;
    }

    importKites(kites: Kite[]) {
        this.setState(({ kites: storeKites }) => ({
            kites: {
                ...storeKites,
                ...kites.reduce((importKites, kite) => {
                    const id = v4();
                    return {
                        ...importKites,
                        [id]: {
                            ...kite,
                            id,
                            name: kite.name + ' (imported)',
                        },
                    };
                }, {}),
            },
        }));
    }
}

export default new Kites();
